import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const key = (pid, scope) => `rfpCollapsed:${pid}:${scope}`;
const EMPTY = { docs: {}, secs: {}, smart: false };
const read = (pid, scope) => { try { return { ...EMPTY, ...(JSON.parse(localStorage.getItem(key(pid, scope))) || {}) }; } catch { return { ...EMPTY }; } };

/** Which cards (annexures / narrative sections) and question sections are folded. The layout is shared per project + view on the
 *  server (so it follows you between devices and the team sees the same tidy view) with a local cache for instant paint.
 *  "Smart fold" folds anything the caller reports as finished unless someone explicitly opened it. */
export const useCollapse = (pid, scope = 'annexures') => {
  const [state, setState] = useState(() => read(pid, scope));
  const [synced, setSynced] = useState(false);
  const dirty = useRef(false);
  const timer = useRef(null);
  useEffect(() => {
    setState(read(pid, scope)); setSynced(false); dirty.current = false;
    if (!pid) return undefined;
    let alive = true;
    axios.get(`${API_URL}/api/rfp/response/projects/${pid}/fold/${scope}`, { withCredentials: true })
      .then((r) => { if (alive && !dirty.current) setState({ docs: r.data.docs || {}, secs: r.data.secs || {}, smart: !!r.data.smart }); })
      .catch(() => {})
      .finally(() => { if (alive) setSynced(true); });
    return () => { alive = false; };
  }, [pid, scope]);
  useEffect(() => {
    if (!pid) return undefined;
    localStorage.setItem(key(pid, scope), JSON.stringify(state));
    if (!dirty.current) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => axios.put(`${API_URL}/api/rfp/response/projects/${pid}/fold/${scope}`, state, { withCredentials: true }).catch(() => {}), 600);
    return () => clearTimeout(timer.current);
  }, [pid, scope, state]);
  const change = (fn) => { dirty.current = true; setState(fn); };
  const secKey = (docId, section) => `${docId}:${section || ''}`;
  const isDocCollapsed = useCallback((docId, done = false) => (state.docs[docId] !== undefined ? !!state.docs[docId] : (state.smart && done)), [state]);
  const isSecCollapsed = useCallback((docId, section, done = false) => { const k = secKey(docId, section); return state.secs[k] !== undefined ? !!state.secs[k] : (state.smart && done); }, [state]);
  const toggleDoc = (docId, current) => change((s) => ({ ...s, docs: { ...s.docs, [docId]: !(current ?? s.docs[docId]) } }));
  const toggleSec = (docId, section, current) => change((s) => ({ ...s, secs: { ...s.secs, [secKey(docId, section)]: !(current ?? s.secs[secKey(docId, section)]) } }));
  const setAllDocs = (docIds, collapsed) => change((s) => ({ ...s, docs: Object.fromEntries(docIds.map((id) => [id, collapsed])) }));
  const setAllSecs = (docId, sections, collapsed) => change((s) => ({ ...s, secs: { ...s.secs, ...Object.fromEntries(sections.map((sec) => [secKey(docId, sec), collapsed])) } }));
  /** Replace the whole layout at once (e.g. "only mine"): docs → folded?, secs → [docId, section, folded?]. */
  const applyLayout = (docs, secs) => change((s) => ({ ...s, docs: { ...docs }, secs: Object.fromEntries(secs.map(([docId, sec, folded]) => [secKey(docId, sec), folded])) }));
  /** Turning smart fold on or off forgets manual choices so the rule applies cleanly (off = everything open). */
  const setSmart = (on) => change(() => ({ docs: {}, secs: {}, smart: !!on }));
  /** Open whatever hides this item so a deep link or gap jump can scroll to it. */
  const reveal = (docId, section) => setState((s) => ({ ...s, docs: { ...s.docs, [docId]: false }, secs: section === undefined ? s.secs : { ...s.secs, [secKey(docId, section)]: false } }));  // local only — a deep link shouldn't rearrange the team's view
  return { isDocCollapsed, isSecCollapsed, toggleDoc, toggleSec, setAllDocs, setAllSecs, applyLayout, reveal, smart: !!state.smart, setSmart, synced };
};

/** An annexure (or a section of one) is finished when every non-pricing row has an accepted answer. */
export const allAccepted = (qs, doneFlags) => qs.length > 0 && qs.every((q) => q.is_pricing || (q.response && doneFlags.includes(q.flag)));

/** True when a row is handed to this person. */
export const isMine = (q, email) => !!email && (q.assignee || '').toLowerCase() === email.toLowerCase();
